import React, { useState } from "react";
import hosys from "@/api/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ClassificationBadge } from "./OvertimeStatusBadge";
import { format } from "date-fns";
import { ca } from "date-fns/locale";

export default function OvertimeValidationModal({ record, onSave, onClose }) {
  const [status, setStatus] = useState(record.status === "detected" ? "pending_review" : record.status);
  const [notes, setNotes] = useState(record.review_notes || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    const now = new Date().toISOString();
    const user = await hosys.auth.me();
    await hosys.entities.OvertimeRecord.update(record.id, {
      status,
      review_notes: notes,
      reviewed_by: user.email,
      reviewed_at: now,
    });
    setSaving(false);
    onSave();
  };

  const formatMins = (mins) => {
    if (!mins && mins !== 0) return "—";
    const h = Math.floor(Math.abs(mins) / 60);
    const m = Math.abs(mins) % 60;
    const sign = mins < 0 ? "-" : "+";
    return `${sign}${h}h ${m.toString().padStart(2, "0")}m`;
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Validar registre d'hores</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
            <p><span className="font-medium">Treballador:</span> {record.user_name}</p>
            <p><span className="font-medium">Data:</span> {format(new Date(record.date), "EEEE d MMMM yyyy", { locale: ca })}</p>
            <div className="flex items-center gap-2 mt-1">
              <span className="font-medium">Classificació:</span>
              <ClassificationBadge classification={record.classification} />
            </div>
            <div className="grid grid-cols-3 gap-2 mt-2 text-center">
              <div className="bg-white rounded p-2 border">
                <p className="text-xs text-gray-500">Prevista</p>
                <p className="font-bold text-sm">{Math.floor(record.planned_work_time / 60)}h {record.planned_work_time % 60}m</p>
              </div>
              <div className="bg-white rounded p-2 border">
                <p className="text-xs text-gray-500">Treballada</p>
                <p className="font-bold text-sm">{Math.floor(record.worked_time / 60)}h {record.worked_time % 60}m</p>
              </div>
              <div className={`rounded p-2 border ${record.time_difference > 0 ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                <p className="text-xs text-gray-500">Diferència</p>
                <p className={`font-bold text-sm ${record.time_difference > 0 ? "text-red-700" : "text-green-700"}`}>{formatMins(record.time_difference)}</p>
              </div>
            </div>
            {record.attendance_incidents?.length > 0 && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                <p className="font-medium text-yellow-800 mb-1">Incidències:</p>
                {[].concat(record.attendance_incidents).map((i, idx) => <p key={`iidx-${idx}`} className="text-yellow-700">• {i}</p>)}
              </div>
            )}
          </div>

          <div>
            <Label className="text-xs">Acció de validació</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending_review">Pendent de revisió</SelectItem>
                <SelectItem value="approved_as_overtime">Aprovar com a hora extraordinària</SelectItem>
                <SelectItem value="approved_as_complementary">Aprovar com a hora complementària</SelectItem>
                <SelectItem value="rejected">Rebutjar (error de fitxatge)</SelectItem>
                <SelectItem value="compensated_with_rest">Compensar amb descans</SelectItem>
                <SelectItem value="paid">Marcar com pagat</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs">Notes de la validació</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Motiu, acord amb treballador..." className="h-20 text-sm" />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel·lar</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#7B2D3F] hover:bg-[#6a2636] text-white">
            {saving ? "Desant..." : "Validar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}